import { create } from 'zustand';
import { TeamMember, fetchTeamMembers } from '../services/teamMembers/teamMembersService';

export interface UserDisplayInfo {
  name: string | null;
  email: string | null;
  profilePictureUrl: string | null;
  displayName: string; // Always has a value (fallback to email prefix or User-XXXX)
}

interface TeamMembersStore {
  // State
  teamMembers: TeamMember[];
  teamMembersMap: Map<string, TeamMember>;
  isLoading: boolean;
  error: string | null;
  isLoaded: boolean;

  // Actions
  load: () => Promise<void>;
  reload: () => Promise<void>;
  isDataLoaded: () => boolean;
  getTeamMember: (userId: string) => TeamMember | undefined;
  getUserDisplayInfo: (userId: string) => UserDisplayInfo;
  clear: () => void;
}

export const useTeamMembersStore = create<TeamMembersStore>((set, get) => ({
  // State
  teamMembers: [],
  teamMembersMap: new Map(),
  isLoading: false,
  error: null,
  isLoaded: false,

  // Actions
  load: async () => {
    const state = get();
    if (state.isLoading || state.isLoaded) return;

    set({ isLoading: true, error: null });

    try {
      const members = await fetchTeamMembers();
      const membersMap = new Map(members.map(m => [m.id, m]));

      set({
        teamMembers: members,
        teamMembersMap: membersMap,
        isLoading: false,
        isLoaded: true,
        error: null,
      });
    } catch (error) {
      console.error('Failed to load team members:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load team members',
      });
    }
  },

  reload: async () => {
    set({ isLoaded: false });
    await get().load();
  },

  isDataLoaded: () => {
    const state = get();
    return state.isLoaded && !state.isLoading;
  },

  getTeamMember: (userId: string) => {
    return get().teamMembersMap.get(userId);
  },

  getUserDisplayInfo: (userId: string): UserDisplayInfo => {
    const member = get().teamMembersMap.get(userId);

    if (member) {
      // Use name, or email prefix (before @), or fallback to User-XXXX
      const emailPrefix = member.email ? member.email.split('@')[0] : null;
      return {
        name: member.name,
        email: member.email,
        profilePictureUrl: member.profile_picture_url,
        displayName: member.name || emailPrefix || `User-${userId.slice(-4)}`,
      };
    }

    // User not found in team members (shouldn't happen normally)
    return {
      name: null,
      email: null,
      profilePictureUrl: null,
      displayName: `User-${userId.slice(-4)}`,
    };
  },

  clear: () => {
    set({
      teamMembers: [],
      teamMembersMap: new Map(),
      isLoading: false,
      error: null,
      isLoaded: false,
    });
  },
}));
